import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  MenuItem,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Link,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
} from '@mui/material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const KIND_LABEL = {
  customer_reply: 'Customer update',
  support_external: 'Support (external)',
  support_internal: 'Support (internal)',
};

async function downloadTicketFile(ticketId, docId, fileName) {
  const r = await api.get(`/helpdesk/tickets/${ticketId}/documents/${docId}/download`, {
    responseType: 'blob',
  });
  const blob = r.data instanceof Blob ? r.data : new Blob([r.data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'attachment';
  a.click();
  window.URL.revokeObjectURL(url);
}

export default function HelpDeskTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const [customerBody, setCustomerBody] = useState('');
  const [supportBody, setSupportBody] = useState('');
  const [supportKind, setSupportKind] = useState('support_external');
  const [postError, setPostError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/helpdesk/tickets/${id}`)
      .then((r) => {
        const d = r.data?.data;
        setTicket(d);
        setStatus(d?.status || '');
      })
      .catch(() => setTicket(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get('/helpdesk/ticket-statuses')
      .then((r) => setStatusOptions(r.data?.data?.options || []))
      .catch(() => setStatusOptions([]));
  }, []);

  const statusMap = useMemo(() => {
    const m = new Map();
    for (const s of statusOptions) m.set(String(s.code), s);
    return m;
  }, [statusOptions]);

  const isSupport =
    user?.is_admin || hasPermission('helpdesk_support') || hasPermission('helpdesk_modify');

  const isRequester = ticket && user && ticket.created_by === user.id;
  const ticketOpen = ticket && !['closed', 'resolved'].includes(ticket.status);

  const assignMe = async () => {
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${id}/assign-me`);
      load();
    } finally {
      setBusy(false);
    }
  };

  const saveStatus = async () => {
    setBusy(true);
    try {
      await api.patch(`/helpdesk/tickets/${id}`, { status });
      load();
    } finally {
      setBusy(false);
    }
  };

  const postCustomerUpdate = async (e) => {
    e?.preventDefault?.();
    setPostError('');
    const text = customerBody.trim();
    if (!text) {
      setPostError('Enter an update message.');
      return;
    }
    const input = document.getElementById('customer-update-files');
    const files = input?.files ? Array.from(input.files) : [];
    const fd = new FormData();
    fd.append('body', text);
    files.forEach((f) => fd.append('attachments', f));
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${id}/updates`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCustomerBody('');
      if (input) input.value = '';
      load();
    } catch (err) {
      setPostError(err.response?.data?.message || 'Failed to submit update.');
    } finally {
      setBusy(false);
    }
  };

  const postSupportUpdate = async (e) => {
    e?.preventDefault?.();
    setPostError('');
    const text = supportBody.trim();
    if (!text) {
      setPostError('Enter an update message.');
      return;
    }
    const input = document.getElementById('support-update-files');
    const files = input?.files ? Array.from(input.files) : [];
    const fd = new FormData();
    fd.append('body', text);
    fd.append('kind', supportKind);
    files.forEach((f) => fd.append('attachments', f));
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${id}/updates`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSupportBody('');
      if (input) input.value = '';
      load();
    } catch (err) {
      setPostError(err.response?.data?.message || 'Failed to submit update.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Ticket not found.</Typography>
        <Button onClick={() => navigate('/helpdesk/my')}>Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      <Typography variant="h5" gutterBottom>
        {ticket.ticket_number}
      </Typography>
      <Chip
        label={statusMap.get(ticket.status)?.label || ticket.status}
        color={statusMap.get(ticket.status)?.ui_color || 'default'}
        sx={{ mr: 1 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Severity: {ticket.severity?.name || '—'}
        {ticket.severity?.turnaround_minutes != null &&
          ` (target ~ ${ticket.severity.turnaround_minutes} min)`}
      </Typography>
      <Typography variant="h6" sx={{ mt: 2 }}>
        {ticket.subject}
      </Typography>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Original request
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {ticket.requester
            ? `${ticket.requester.first_name || ''} ${ticket.requester.last_name || ''}`.trim()
            : '—'}
        </Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{ticket.description || '—'}</Typography>
      </Paper>

      {Array.isArray(ticket.updates) && ticket.updates.length > 0 && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Activity</Typography>
          {ticket.updates.map((u) => (
            <Paper key={u.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip size="small" label={KIND_LABEL[u.kind] || u.kind} variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {u.author
                    ? `${u.author.first_name || ''} ${u.author.last_name || ''}`.trim() || u.author.email
                    : '—'}{' '}
                  · {u.createdAt ? new Date(u.createdAt).toLocaleString() : ''}
                </Typography>
              </Stack>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{u.body}</Typography>
              {u.documents?.length > 0 && (
                <List dense sx={{ mt: 1 }}>
                  {u.documents.map((d) => (
                    <ListItem key={d.id} disablePadding>
                      <ListItemText
                        primary={d.title || d.file_name}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondary={
                          <Link
                            component="button"
                            type="button"
                            variant="body2"
                            onClick={() => downloadTicketFile(id, d.id, d.file_name)}
                          >
                            Download
                          </Link>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          ))}
        </Stack>
      )}

      <Typography variant="body2" sx={{ mt: 2 }}>
        Owner:{' '}
        {ticket.owner ? `${ticket.owner.first_name} ${ticket.owner.last_name}` : 'Not assigned'}
      </Typography>

      {isSupport && !ticket.owner_user_id && ticketOpen && (
        <Button sx={{ mt: 2 }} variant="contained" onClick={assignMe} disabled={busy}>
          Assign it to me
        </Button>
      )}

      {isSupport && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            {(statusOptions.length ? statusOptions : [{ code: ticket.status, label: ticket.status, ui_color: 'default' }]).map((s) => (
              <MenuItem key={s.code} value={s.code}>
                {s.label || s.code}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={saveStatus} disabled={busy}>
            Update status
          </Button>
        </Box>
      )}

      {postError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {postError}
        </Alert>
      )}

      {isRequester && ticketOpen && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Add your update
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Describe what changed or what you need. You can attach pictures, PDFs, or Word documents. Submitting sets
            the ticket to <strong>Customer updated</strong> for the support team.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Update"
            value={customerBody}
            onChange={(e) => setCustomerBody(e.target.value)}
            disabled={busy}
          />
          <Button variant="outlined" component="label" sx={{ mt: 1, mr: 1 }}>
            Attach files
            <input id="customer-update-files" type="file" multiple hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx" />
          </Button>
          <Button variant="contained" sx={{ mt: 1 }} onClick={postCustomerUpdate} disabled={busy}>
            Submit update
          </Button>
        </Paper>
      )}

      {isSupport && ticketOpen && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Support reply
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Update (external)</strong> is visible to the customer and sets status to{' '}
            <strong>Wait on customer</strong>. <strong>Update (internal)</strong> is only visible to support staff and
            does not change status.
          </Typography>
          <RadioGroup row value={supportKind} onChange={(e) => setSupportKind(e.target.value)}>
            <FormControlLabel value="support_external" control={<Radio size="small" />} label="Update (external)" />
            <FormControlLabel value="support_internal" control={<Radio size="small" />} label="Update (internal)" />
          </RadioGroup>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Message"
            value={supportBody}
            onChange={(e) => setSupportBody(e.target.value)}
            disabled={busy}
            sx={{ mt: 1 }}
          />
          <Button variant="outlined" component="label" sx={{ mt: 1, mr: 1 }}>
            Attach files
            <input id="support-update-files" type="file" multiple hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx" />
          </Button>
          <Button variant="contained" sx={{ mt: 1 }} onClick={postSupportUpdate} disabled={busy}>
            Submit
          </Button>
        </Paper>
      )}

      {!isSupport && ticket.created_by === user?.id && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            You can close this ticket when resolved.
          </Typography>
          <Button
            variant="outlined"
            onClick={async () => {
              setStatus('closed');
              await api.patch(`/helpdesk/tickets/${id}`, { status: 'closed' });
              load();
            }}
            disabled={ticket.status === 'closed' || busy}
          >
            Close ticket
          </Button>
        </Box>
      )}

      {ticket.documents?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">Original attachments</Typography>
          <List dense>
            {ticket.documents.map((d) => (
              <ListItem key={d.id}>
                <ListItemText
                  primary={d.title || d.file_name}
                  secondary={
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => downloadTicketFile(id, d.id, d.file_name)}
                    >
                      Download
                    </Link>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
